// React not used explicitly
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { 
  TagIcon,
  PlusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { ProductVariant } from '../types/product'
import { GET_VARIANT_OPTION_GROUPS_BY_VARIANT, GET_OPTION_ITEMS_BY_GROUP } from '../graphql/product-queries'
import { toApiInt } from '../lib/utils'

// Component to display option items for a specific option group
const OptionItemsPreview = ({ optionGroupId }: { optionGroupId: number }) => {
  const { data: optionItemsData, loading } = useQuery(GET_OPTION_ITEMS_BY_GROUP, {
    variables: { optionGroupId: toApiInt(optionGroupId) },
    skip: !optionGroupId
  })

  const optionItems = optionItemsData?.optionItemsByGroup || []

  if (loading) {
    return (
      <div className="mt-4">
        <dt className="text-sm font-medium text-gray-500 mb-2">Available Options</dt>
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    )
  }

  if (optionItems.length === 0) {
    return (
      <div className="mt-4">
        <dt className="text-sm font-medium text-gray-500 mb-2">Available Options</dt>
        <div className="text-sm text-gray-500">No options available</div>
      </div>
    )
  }

  return (
    <div className="mt-4">
      <dt className="text-sm font-medium text-gray-500 mb-2">Available Options</dt>
      <div className="flex flex-wrap gap-2">
        {optionItems.slice(0, 5).map((item: any) => (
          <span
            key={item.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
          >
            {item.label}
            {item.price_delta_cents !== 0 && (
              <span className="ml-1 text-gray-500">
                ({item.price_delta_cents > 0 ? '+' : ''}${(item.price_delta_cents / 100).toFixed(2)})
              </span>
            )}
            {item.default_selected && (
              <span className="ml-1 text-green-600 font-medium">•</span>
            )}
          </span>
        ))}
        {optionItems.length > 5 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            +{optionItems.length - 5} more
          </span>
        )}
      </div>
    </div>
  )
}

interface VariantOptionGroupsTabProps {
  variant: ProductVariant
  productId: number
}

const VariantOptionGroupsTab = ({ variant, productId }: VariantOptionGroupsTabProps) => {
  const navigate = useNavigate()

  const handleOptionGroupClick = (optionGroupId: number) => {
    navigate(`/option-groups/${optionGroupId}`, { 
      state: { 
        from: 'variant-detail',
        variantId: variant.id,
        productId: productId
      }
    })
  }

  const { data: variantOptionGroupsData, loading } = useQuery(GET_VARIANT_OPTION_GROUPS_BY_VARIANT, {
    variables: { productVariantId: toApiInt(variant.id) },
    skip: !variant.id
  })

  const variantOptionGroups = variantOptionGroupsData?.variantOptionGroupsByVariant || []

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Option Groups</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage which option groups are available for this variant
          </p>
        </div>
        <Link
          to={`/products/${productId}/variants/${variant.id}/option-groups/create`}
          className="btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Option Group
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : variantOptionGroups.length === 0 ? (
        /* Empty State */
        <div className="text-center py-12">
          <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No option groups</h3>
          <p className="mt-1 text-sm text-gray-500">
            This variant doesn't have any option groups configured yet.
          </p>
          <div className="mt-6">
            <Link
              to={`/products/${productId}/variants/${variant.id}/option-groups/create`}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Option Group
            </Link>
          </div>
        </div>
      ) : (
        /* Option Groups List */
        <div className="space-y-4">
          {variantOptionGroups.map((vog: any) => {
            const optionGroup = vog.option_group
            
            if (!optionGroup) {
              return (
                <div key={vog.id} className="card p-4">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Unknown Option Group</p>
                      <p className="text-sm text-gray-500">Option Group ID: {vog.option_group_id}</p>
                    </div>
                  </div>
                </div>
              )
            }

            return (
              <div 
                key={vog.id} 
                className="card hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleOptionGroupClick(optionGroup.id)}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {/* Option Group Name - Prominent Display */}
                      <div className="flex items-center mb-4">
                        <TagIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <h3 className="text-lg font-medium text-gray-900">{optionGroup.name}</h3>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Selection Rules</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            Min: {optionGroup.min_select}, Max: {optionGroup.max_select}
                          </dd>
                        </div>
                        
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Sort Order</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {vog.sort_order || 'Auto'}
                          </dd>
                        </div>
                        
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Status</dt>
                          <dd className="mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              optionGroup.active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {optionGroup.active ? 'Active' : 'Inactive'}
                            </span>
                          </dd>
                        </div>
                      </div>

                      {/* Option Items Preview */}
                      <OptionItemsPreview optionGroupId={optionGroup.id} />

                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}

export default VariantOptionGroupsTab
